import { PageContainer } from "@/components/layout/PageContainer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { Car } from "@shared/schemas/cars";
import { SafetyInfoBox } from "@/components/cars/SafetyInfoBox";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import {
  AlertCircle,
  ArrowLeft,
  Car as CarIcon,
  Calendar,
  Clock,
  Eye,
  Fuel,
  Gauge,
  Heart,
  Info,
  MapPin,
  ShieldCheck,
  User,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  CheckCircle2,
  AlertTriangle,
  FileText,
} from "lucide-react";
import { useState } from "react";
import { useLocation, useRoute } from "wouter";

export default function CarDetail() {
  const [match, params] = useRoute<{ id: string }>("/cars/:id");
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);

  if (!match || !params) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">
              유효하지 않은 차량 ID입니다
            </p>
            <Button variant="outline" onClick={() => setLocation("/cars")}>
              목록으로
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const carId = params.id;

  // BusinessDirectoryDetail 패턴 사용: queryClient의 기본 queryFn이 자동으로 unwrapPayload 처리
  // API는 snake_case로 반환하므로 any 타입 사용
  const { data: carData, isLoading, error } = useQuery<any>({
    queryKey: [`/api/cars/${carId}`],
    enabled: !!carId,
  });

  // 디버깅: API 응답 확인
  if (carData) {
    console.log('[CarDetail] carData:', carData);
  }
  if (error) {
    console.error('[CarDetail] error:', error);
  }

  // API 응답을 그대로 사용 (queryClient가 이미 언래핑함)
  const car: any = carData || null;

  if (isLoading) {
    return (
      <PageContainer size="default">
        <div className="animate-pulse space-y-4">
          <div className="h-96 bg-muted rounded-lg" />
          <div className="h-32 bg-muted rounded-lg" />
        </div>
      </PageContainer>
    );
  }

  if (!car) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">차량을 찾을 수 없습니다</h2>
          <Button onClick={() => setLocation("/cars")} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            목록으로 돌아가기
          </Button>
        </Card>
      </PageContainer>
    );
  }

  const formatPrice = (priceCents: number | null | undefined) => {
    if (priceCents === null || priceCents === undefined) return "가격 문의";
    if (priceCents === 0) return "무료나눔";
    if (priceCents === -1) return "가격협의";
    return `$${Math.round(priceCents / 100).toLocaleString()}`;
  };

  const formatMileage = (mileage: number | null) => {
    if (!mileage) return "정보 없음";
    return `${mileage.toLocaleString()}km`;
  };

  const isOwner = user?.uid === car?.user_id;

  // Generate key highlights from car data
  const generateKeyHighlights = () => {
    const highlights: string[] = [];
    if (car?.rwc_available) highlights.push("Full service history");
    if (car?.description?.toLowerCase().includes("bluetooth")) highlights.push("Bluetooth");
    if (car?.description?.toLowerCase().includes("camera") || car?.description?.toLowerCase().includes("카메라")) {
      highlights.push("Reversing camera");
    }
    if (car?.description?.toLowerCase().includes("gps") || car?.description?.toLowerCase().includes("navigation")) {
      highlights.push("GPS (satellite navigation)");
    }
    if (car?.transmission === "Auto") highlights.push("Automatic transmission");
    if (car?.fuel_type === "Electric" || car?.fuel_type === "Hybrid") {
      highlights.push("Eco-friendly");
    }
    if (car?.rego_status === "Yes") highlights.push("Registered");
    return highlights.length > 0 ? highlights : ["Well maintained", "Good condition"];
  };

  const keyHighlights = generateKeyHighlights();

  // Vehicle description
  const vehicleDescription = `${car?.make || ""} ${car?.model || ""} ${car?.year || ""} ${car?.body_type || ""} ${car?.transmission || ""} ${car?.fuel_type || ""}`.trim();

  // 이미지 URL 정규화 및 검증
  // R2 경로(/api/media/...)와 외부 URL 모두 지원
  const normalizeImageUrl = (url: string | null | undefined): string | null => {
    if (!url) return null;

    // R2 경로인 경우 그대로 반환 (이미 정규화됨)
    if (url.startsWith('/api/media/')) {
      return url;
    }

    // 절대 URL인 경우
    try {
      const urlObj = new URL(url);

      // Unsplash 이미지인 경우 파라미터 정리
      if (urlObj.hostname.includes('unsplash.com')) {
        urlObj.searchParams.delete('h');
        urlObj.searchParams.delete('fit');
        if (!urlObj.searchParams.has('w')) {
          urlObj.searchParams.set('w', '800');
        }
        return urlObj.toString();
      }

      // 다른 외부 URL도 그대로 반환
      return url;
    } catch {
      // 상대 경로이거나 잘못된 URL인 경우 그대로 반환
      return url;
    }
  };

  const allImages = (() => {
    const imagesArray = Array.isArray(car?.images) ? car.images.map(normalizeImageUrl).filter(Boolean) as string[] : [];
    const primaryImg = normalizeImageUrl(car?.primary_image);

    // primary_image가 있고 images 배열에 없으면 첫 번째로 추가
    if (primaryImg && !imagesArray.includes(primaryImg)) {
      return [primaryImg, ...imagesArray];
    }

    // primary_image가 없고 images 배열만 있으면 그대로 사용
    if (imagesArray.length > 0) {
      return imagesArray;
    }

    // primary_image만 있으면 배열로 변환
    if (primaryImg) {
      return [primaryImg];
    }

    return [];
  })();

  return (
    <PageContainer size="default">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => setLocation("/cars")}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        목록으로
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left - Images & Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header Section */}
          <div>
            <h1 className="text-3xl font-bold mb-2">
              {car?.year} {car?.make} {car?.model}
            </h1>
            <div className="flex items-center gap-2 text-muted-foreground">
              <span>{car?.state}</span>
              <span>•</span>
              <span>{car?.is_dealer_listing ? "Dealer used" : "Private sale"}</span>
            </div>
          </div>

          {/* Images */}
          <Card>
            <CardContent className="p-0">
              {allImages.length > 0 ? (
                <>
                  <div className="relative h-[500px] bg-muted overflow-hidden rounded-t-lg">
                    <img
                      src={(() => {
                        const imgUrl = allImages[selectedImageIndex] || allImages[0];
                        // R2 경로인 경우 절대 URL로 변환
                        if (imgUrl && imgUrl.startsWith('/api/media/')) {
                          return imgUrl.startsWith('http') ? imgUrl : `${window.location.origin}${imgUrl}`;
                        }
                        return imgUrl;
                      })()}
                      alt={`${car?.make || ""} ${car?.model || ""}`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      onError={(e) => {
                        console.error("Image load error:", e.currentTarget.src);
                        // 이미지 로드 실패 시 SVG placeholder로 대체
                        e.currentTarget.style.display = "none";
                        const parent = e.currentTarget.parentElement;
                        if (parent && !parent.querySelector(".image-placeholder")) {
                          const placeholder = document.createElement("div");
                          placeholder.className = "image-placeholder w-full h-full flex items-center justify-center bg-muted";
                          placeholder.innerHTML = `
                            <div class="text-center">
                              <svg class="w-32 h-32 mx-auto text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"></path>
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"></path>
                              </svg>
                              <p class="text-sm text-muted-foreground mt-2">이미지를 불러올 수 없습니다</p>
                            </div>
                          `;
                          parent.appendChild(placeholder);
                        }
                      }}
                    />
                    {allImages.length > 1 && (
                      <Badge className="absolute top-4 left-4 bg-black/50 text-white">
                        {selectedImageIndex + 1} / {allImages.length}
                      </Badge>
                    )}
                    {car?.is_premium && (
                      <Badge className="absolute top-4 right-4 bg-primary">
                        프리미엄
                      </Badge>
                    )}
                    {car?.is_dealer_listing && (
                      <Badge className="absolute top-16 right-4 bg-primary">
                        딜러
                      </Badge>
                    )}
                  </div>
                  {allImages.length > 1 && (
                    <div className="p-4 flex gap-2 overflow-x-auto">
                      {allImages.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedImageIndex(idx)}
                          className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 ${
                            idx === selectedImageIndex
                              ? "border-primary"
                              : "border-transparent opacity-60 hover:opacity-100"
                          }`}
                        >
                          <img
                            src={(() => {
                              // R2 경로인 경우 절대 URL로 변환
                              if (img && img.startsWith('/api/media/')) {
                                return img.startsWith('http') ? img : `${window.location.origin}${img}`;
                              }
                              return img;
                            })()}
                            alt={`${car?.make || ""} ${car?.model || ""} ${idx + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                              console.error("Thumbnail load error:", e.currentTarget.src);
                              // 썸네일 로드 실패 시 SVG placeholder로 대체
                              e.currentTarget.style.display = "none";
                              const parent = e.currentTarget.parentElement;
                              if (parent && !parent.querySelector(".thumbnail-placeholder")) {
                                const placeholder = document.createElement("div");
                                placeholder.className = "thumbnail-placeholder w-full h-full flex items-center justify-center bg-muted";
                                placeholder.innerHTML = `
                                  <svg class="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"></path>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"></path>
                                  </svg>
                                `;
                                parent.appendChild(placeholder);
                              }
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="h-[500px] bg-muted flex items-center justify-center rounded-lg">
                  <CarIcon className="w-32 h-32 text-muted-foreground" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Key Specs Summary */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">주행거리</p>
                    <p className="font-semibold">{formatMileage(car?.mileage)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CarIcon className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">차종</p>
                    <p className="font-semibold">{car?.body_type || "N/A"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Fuel className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">연료</p>
                    <p className="font-semibold">{car?.fuel_type || "N/A"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Gauge className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">변속기</p>
                    <p className="font-semibold">{car?.transmission || "N/A"}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Key Highlights */}
          {keyHighlights.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>주요 특징</CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      BETA
                    </Badge>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        <ThumbsUp className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        <ThumbsDown className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  판매자 코멘트와 차량 사양을 AI로 분석하여 생성되었습니다.{" "}
                  <Button variant="link" className="h-auto p-0 text-xs">
                    더 알아보기
                  </Button>
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {keyHighlights.map((highlight, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {highlight}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comments from Dealer */}
          {car?.is_dealer_listing && car?.description && (
            <Card>
              <CardHeader>
                <CardTitle>딜러 코멘트</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {showFullDescription || car.description.length < 200
                    ? car.description
                    : car.description.substring(0, 200) + "..."}
                </p>
                {car.description.length > 200 && (
                  <Button
                    variant="link"
                    className="mt-2 p-0 h-auto"
                    onClick={() => setShowFullDescription(!showFullDescription)}
                  >
                    {showFullDescription ? "접기" : "더 보기"}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Car Details - Tabs */}
          <Card>
            <CardHeader>
              <CardTitle>차량 상세 정보</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="overview">개요</TabsTrigger>
                  <TabsTrigger value="features">기능</TabsTrigger>
                  <TabsTrigger value="specifications">사양</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium mb-1">차량 설명</p>
                      <p className="text-sm text-muted-foreground">{vehicleDescription}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">동력원</p>
                      <p className="text-sm text-muted-foreground">
                        {car?.fuel_type === "Electric" ? "전기차 (EV)" : car?.fuel_type === "Hybrid" ? "하이브리드" : "내연기관 (ICE)"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">보험료</p>
                      <Button variant="link" className="h-auto p-0 text-sm text-primary">
                        Budget Direct에서 맞춤 보험료 견적 받기
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                    {car.color && (
                      <>
                        <div>
                          <p className="text-sm font-medium mb-1">외장 색상</p>
                          <p className="text-sm text-muted-foreground uppercase">{car.color}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium mb-1">내장 색상</p>
                          <p className="text-sm text-muted-foreground">미지정</p>
                        </div>
                      </>
                    )}
                    <div>
                      <p className="text-sm font-medium mb-1">차종</p>
                      <p className="text-sm text-muted-foreground">
                        {car?.body_type || "미지정"}, {car?.transmission === "Auto" ? "자동" : car?.transmission || "미지정"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">변속기</p>
                      <p className="text-sm text-muted-foreground">
                        {car?.transmission === "Auto" ? "자동" : car?.transmission || "미지정"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">엔진</p>
                      <p className="text-sm text-muted-foreground">
                        {car?.fuel_type || "미지정"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">연비 (복합)‡</p>
                      <p className="text-sm text-muted-foreground">정보 없음</p>
                    </div>
                    {car?.rego_status && (
                      <>
                        <div>
                          <p className="text-sm font-medium mb-1">등록번호판</p>
                          <p className="text-sm text-muted-foreground">제공 안 됨</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium mb-1">등록 만료일</p>
                          <p className="text-sm text-muted-foreground">
                            {car.rego_expiry_date ? new Date(car.rego_expiry_date).toLocaleDateString('ko-KR') : "미지정"}
                          </p>
                        </div>
                      </>
                    )}
                    <div>
                      <p className="text-sm font-medium mb-1 flex items-center gap-1">
                        제조일
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="w-3 h-3 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">차량 제조일</p>
                          </TooltipContent>
                        </Tooltip>
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {car?.year ? `January ${car.year}` : "Not specified"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1 flex items-center gap-1">
                        준수일
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="w-3 h-3 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">호주 도로 주행 승인일</p>
                          </TooltipContent>
                        </Tooltip>
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {car?.year ? `April ${car.year}` : "Not specified"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">모델 연도</p>
                      <p className="text-sm text-muted-foreground">MY{car?.year?.toString().slice(-2) || "N/A"}</p>
                    </div>
                    {car?.is_dealer_listing && (
                      <div>
                        <p className="text-sm font-medium mb-1">딜러 재고 번호</p>
                        <p className="text-sm text-muted-foreground">{car.id.slice(0, 8)}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Features Tab */}
                <TabsContent value="features" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {car?.description || "기능이 나열되지 않았습니다. 자세한 정보는 판매자에게 문의하세요."}
                    </p>
                  </div>
                </TabsContent>

                {/* Specifications Tab */}
                <TabsContent value="specifications" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium mb-1">브랜드</p>
                      <p className="text-sm text-muted-foreground">{car?.make}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">모델</p>
                      <p className="text-sm text-muted-foreground">{car?.model}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">연식</p>
                      <p className="text-sm text-muted-foreground">{car?.year}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">주행거리</p>
                      <p className="text-sm text-muted-foreground">{formatMileage(car?.mileage)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">차종</p>
                      <p className="text-sm text-muted-foreground">{car?.body_type || "미지정"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">변속기</p>
                      <p className="text-sm text-muted-foreground">{car?.transmission || "미지정"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">연료</p>
                      <p className="text-sm text-muted-foreground">{car?.fuel_type || "미지정"}</p>
                    </div>
                    {car?.vin && (
                      <div>
                        <p className="text-sm font-medium mb-1">VIN</p>
                        <p className="text-sm text-muted-foreground font-mono">{car.vin}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Pricing Insights */}
          <Card>
            <CardHeader>
              <CardTitle>가격 인사이트</CardTitle>
            </CardHeader>
            <CardContent>
              <Button variant="link" className="h-auto p-0 text-sm text-primary">
                가격 변동 이력 확인
                <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </CardContent>
          </Card>

          {/* Vehicle History Report */}
          <Card>
            <CardHeader>
              <CardTitle>차량 이력 보고서</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">전손 또는 도난</p>
                  <p className="text-xs text-muted-foreground">차량 상태 확인</p>
                </div>
                <Button variant="link" className="h-auto p-0 text-sm text-primary">
                  상태 확인
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">미상환 금융</p>
                  <p className="text-xs text-muted-foreground">미상환 금융 확인</p>
                </div>
                <Button variant="link" className="h-auto p-0 text-sm text-primary">
                  보고서 보기
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">등록 검증</p>
                  <p className="text-xs text-muted-foreground">등록 상태 확인</p>
                </div>
                <Button variant="link" className="h-auto p-0 text-sm text-primary">
                  등록 정보 보기
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">주행거리 불일치</p>
                  <p className="text-xs text-muted-foreground">주행거리 역주행 확인</p>
                </div>
                <Button variant="link" className="h-auto p-0 text-sm text-primary">
                  주행거리 확인
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Description (for private sellers) */}
          {!car?.is_dealer_listing && car?.description && (
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {car?.description}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Safety Info */}
          <SafetyInfoBox car={car} />

          {/* Stats */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span>조회 {car?.views || 0}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Heart className="w-4 h-4" />
                  <span>찜 {car?.favorites || 0}</span>
                </div>
                {car?.created_at && (
                  <span>
                    {formatDistanceToNow(new Date(car.created_at), {
                      addSuffix: true,
                      locale: ko,
                    })}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right - Seller Info & Actions */}
        <div className="space-y-6">
          {/* Price Card */}
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-primary mb-2">
                  {formatPrice(car?.price)}
                </p>
                {car?.price_type === "negotiable" && (
                  <p className="text-sm text-muted-foreground">가격협의</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Seller Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                판매자
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarImage src={undefined} />
                  <AvatarFallback>
                    {car?.seller_nickname?.[0] || car?.seller_display_name?.[0] || "?"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">
                    {car?.seller_nickname || car?.seller_display_name || "익명"}
                  </p>
                  {car?.is_dealer_listing && (
                    <Badge variant="default" className="mt-1">
                      딜러
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                <p>
                  {car?.city}
                  {car?.suburb && `, ${car.suburb}`}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardContent className="p-6 space-y-3">
              {!isOwner && (
                <>
                  <Button className="w-full" size="lg">
                    문의하기
                  </Button>
                  <Button variant="outline" className="w-full">
                    <Heart className="w-4 h-4 mr-2" />
                    찜하기
                  </Button>
                </>
              )}
              {isOwner && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setLocation(`/cars/edit/${car.id}`)}
                >
                  수정하기
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </PageContainer>
  );
}
